import type { SavedDestination } from "@solomon-apps/shared";
import { List, ListItem } from "@solomon-apps/shared/src/components/Data/List";
import { ResponsiveAddress } from "@solomon-apps/shared/src/components/ResponsiveAddress";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { ConfirmModal } from "@solomon-apps/shared/src/components/ui/confirm-modal";
import { Skeleton } from "@solomon-apps/shared/src/components/ui/skeleton";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { ArrowShortLeftIcon } from "@solomon-labs/design/components/icons";
import {
    Address,
    Button as DesignButton
} from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { DisabledReason } from "@/components/DisabledReason";
import { VIEW_ONLY_REASON, ViewOnlyHint } from "@/components/ViewOnly";
import { useClientPositions } from "@/hooks/useClientPositions";
import {
    useCreateSavedDestination,
    useDeleteSavedDestination,
    useSavedDestinations
} from "@/hooks/useClientSavedDestinations";
import { useViewOnly } from "@/hooks/useViewOnly";
import {
    type Holding,
    holdingDestinationAddresses
} from "@/pages/Positions/holdings";
import {
    isClientConnectionError,
    resolveClientFormError
} from "@/utils/clientFormErrors";

import {
    SortableColHead,
    type SortDirection
} from "./Positions/components/SortableColHead";
import { useHoldings } from "./Positions/useHoldings";
import { AddDestinationForm } from "./RewardDestinations/AddDestinationForm";
import {
    ADD_DESTINATION_PATH,
    destinationDisplayName,
    REWARD_DESTINATIONS_PATH,
    savedDestinationLabel
} from "./RewardDestinations/destinationInput";
import { DestinationNameCell } from "./RewardDestinations/DestinationNameCell";
import { formatUsd } from "./shared/format";

/**
 * Shared column template for the Reward Destinations table — used by both
 * the header row and each `ListItem` row so the columns line up:
 * name · address · USDv balance · positions using · (trailing action
 * cell). Name + Address are separate
 * single-line columns; numeric columns are right-aligned, mirroring
 * PositionsPage.
 */
const ROW_GRID =
    "grid grid-cols-[minmax(120px,1fr)_minmax(140px,1.6fr)_minmax(104px,1fr)_minmax(96px,0.9fr)_28px] items-center gap-4";

/**
 * Row view-model for the Reward Destinations table. Derived from a
 * `SavedDestination` joined against the positions that route their rewards
 * to its address. The USDv balance is a placeholder (the saved destination
 * has no wallet-balance field of its own); see `buildRows`.
 */
export interface DestinationRow {
    uuid: string;
    address: string;
    /** Custom label, or "" when the destination has no label. */
    label: string;
    usdvBalance: number;
    positionsUsing: number;
    /** A saved address-book entry (renamable, deletable). False for a
     *  destination a wallet routes to without one, e.g. its own address. */
    saved: boolean;
}

type SortColumn = "name" | "address" | "balance" | "positionsUsing";

/**
 * YaaS Client Console — Reward Destinations page.
 *
 * A dedicated nav home for the org's saved reward destinations — the
 * address book surfaced as "Pick a saved destination" in the Positions
 * reward-destination picker. Mirrors PositionsPage's table layout: a
 * motion wrapper → `<Text variant="h1">` header → a controls row with a
 * "+Add Destination" action → a sortable column-header row over a shared
 * `ROW_GRID` → `<List>`/`<ListItem>` rows with right-aligned numeric cells.
 *
 * Adding happens in-page on `/reward-destinations/add` (like Add a
 * Position on `/positions/add`): the header stays, the controls + table are
 * replaced by `AddDestinationForm`. It writes through
 * `useCreateSavedDestination` (same source as the Positions picker), so a
 * new address is immediately selectable when routing a position's rewards.
 */
export function RewardDestinationsPage(): React.ReactElement {
    const { destinations, isLoading, error, refetch } = useSavedDestinations();
    const { positions } = useClientPositions();
    const createMutation = useCreateSavedDestination();
    const deleteMutation = useDeleteSavedDestination();
    const viewOnly = useViewOnly();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const adding = pathname === ADD_DESTINATION_PATH;
    const holdings = useHoldings(positions);

    // Failures show inline (form / delete dialog), never as toasts.
    const [saveError, setSaveError] = useState<string | null>(null);
    // Set only when the save never reached the server: Retry resends it.
    const [retryAdd, setRetryAdd] = useState<(() => void) | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<DestinationRow | null>(
        null
    );

    // Counted per holding: a wallet under several Programs is one position.
    const rows = useMemo(
        () => buildRows(destinations, holdings),
        [destinations, holdings]
    );

    const handleAdd = async (input: {
        name: string;
        address: string;
    }): Promise<void> => {
        setSaveError(null);
        setRetryAdd(null);
        try {
            await createMutation.mutateAsync(input);
            // Landing back on the list, with the new row in it, is the
            // confirmation; no toast.
            navigate(REWARD_DESTINATIONS_PATH);
        } catch (e) {
            setSaveError(
                resolveClientFormError(
                    e,
                    "savedDestination",
                    "Couldn't save destination."
                )
            );
            if (isClientConnectionError(e)) {
                setRetryAdd(() => (): undefined => void handleAdd(input));
            }
        }
    };

    const handleConfirmDelete = async (): Promise<void> => {
        if (!pendingDelete) return;
        setDeleteError(null);
        try {
            await deleteMutation.mutateAsync(pendingDelete.uuid);
            setPendingDelete(null);
        } catch (e) {
            setDeleteError(
                resolveClientFormError(
                    e,
                    "savedDestination",
                    "Couldn't delete destination."
                )
            );
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col pt-6 w-full"
        >
            {/* Page header */}
            <section className="pb-6">
                <div className="px-6 flex flex-col gap-4">
                    {/* Adding, the page is the add flow: the way back sits
                        above its title as an eyebrow. */}
                    {adding ? (
                        <DesignButton
                            variant="link"
                            interaction="underline"
                            size="sm"
                            leading={<ArrowShortLeftIcon />}
                            render={<Link to={REWARD_DESTINATIONS_PATH} />}
                            className="self-start"
                        >
                            Rewards Destinations
                        </DesignButton>
                    ) : null}
                    <Text variant="h1" className="text-foreground-base">
                        {adding
                            ? "Add Rewards Destination Address"
                            : "Rewards Destinations"}
                    </Text>
                </div>
            </section>

            {adding ? (
                /* Add flow (route: /reward-destinations/add) — replaces the
                   controls + table under the page header, as Add a Position
                   does on /positions/add. Saving is a plain API call (no
                   wallet signature), so unlike /positions/add there is no
                   DesktopOnly gate. */
                <section className="px-6 py-6 flex flex-col gap-6">
                    <AddDestinationForm
                        onAdd={(input) => void handleAdd(input)}
                        onCancel={() => navigate(REWARD_DESTINATIONS_PATH)}
                        busy={createMutation.isPending}
                        error={saveError}
                        onRetry={retryAdd ?? undefined}
                    />
                </section>
            ) : (
                <>
                    {/* Controls row — "+Add Destination" on the right, mirroring
                        Positions' "+New Address". */}
                    <div className="px-6 py-5 flex items-center justify-between gap-4">
                        <Text variant="small" className="text-foreground-muted">
                            {rows.length}{" "}
                            {rows.length === 1 ? "destination" : "destinations"}
                        </Text>
                        <div className="flex flex-col items-end gap-2">
                            <Button
                                variant="raised"
                                interaction="lifted"
                                size="xs"
                                disabled={viewOnly}
                                onClick={() => navigate(ADD_DESTINATION_PATH)}
                            >
                                +Add Destination
                            </Button>
                            {viewOnly && <ViewOnlyHint />}
                        </div>
                    </div>

                    {isLoading && rows.length === 0 ? (
                        <div className="px-6 py-6">
                            <Skeleton className="h-4 w-32 rounded-none" />
                            <Skeleton className="h-3 w-64 rounded-none mt-3" />
                        </div>
                    ) : error && rows.length === 0 ? (
                        <div className="px-6 py-8 flex items-center gap-2 flex-wrap">
                            <Text
                                variant="small"
                                className="text-foreground-muted"
                            >
                                Couldn&rsquo;t load rewards destinations.
                            </Text>
                            <Button
                                type="button"
                                variant="transparent"
                                size="sm"
                                interaction="underline"
                                onClick={refetch}
                            >
                                Retry
                            </Button>
                        </div>
                    ) : (
                        <DestinationsTable
                            rows={rows}
                            viewOnly={viewOnly}
                            onDelete={(row) => setPendingDelete(row)}
                        />
                    )}
                </>
            )}

            <ConfirmModal
                open={pendingDelete !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setPendingDelete(null);
                        setDeleteError(null);
                    }
                }}
                titleVariant="h2"
                title="Delete Rewards Destination?"
                description={
                    // A failed delete says why in place of the usual line.
                    deleteError ??
                    (pendingDelete ? (
                        <>
                            {pendingDelete.label || "This address"}{" "}
                            <ResponsiveAddress
                                inline
                                prefix="("
                                suffix=")"
                                address={pendingDelete.address}
                                className="font-numeric"
                            />{" "}
                            will be removed from your saved destinations.
                        </>
                    ) : undefined)
                }
                confirmLabel={deleteMutation.isPending ? "Deleting…" : "Delete"}
                destructive
                confirmDisabled={deleteMutation.isPending}
                onConfirm={() => void handleConfirmDelete()}
            />
        </motion.div>
    );
}

/* ── Row derivation ──────────────────────────────────────────── */

export function buildRows(
    destinations: SavedDestination[],
    holdings: Holding[]
): DestinationRow[] {
    // Where each holding's rewards actually go: its wallet's destination, or
    // the wallet itself when none is set. One holding = one position to the
    // client, however many Programs sit behind it.
    const walletOf = (h: Holding): Holding["positions"][number]["account"] =>
        h.positions[0].account;
    const effective = (h: Holding): string[] =>
        holdingDestinationAddresses(h.positions);
    const routedTo = (address: string): Holding[] =>
        holdings.filter((h) => effective(h).includes(address));

    // PLACEHOLDER: USDv Balance — the destination wallet's USDv balance is
    // not in the data, so we approximate with the Σ of the routing holdings'
    // manual amounts, each holding once (its records report the same funds,
    // so the max of them, never the sum). Do NOT treat this as the real
    // on-chain wallet balance.
    const balanceOf = (routed: Holding[]): number =>
        routed.reduce(
            (sum, h) =>
                sum +
                Math.max(
                    ...h.positions.map((p) => Number(p.manualUsdvAmount) || 0)
                ),
            0
        );

    const saved: DestinationRow[] = destinations.map((destination) => {
        const routed = routedTo(destination.address);
        return {
            uuid: destination.uuid,
            address: destination.address,
            label: savedDestinationLabel(destination),
            usdvBalance: balanceOf(routed),
            positionsUsing: routed.length,
            saved: true
        };
    });

    // Every destination in use is listed too, saved or not: a wallet that
    // routes to itself (the default), or to an address that was never saved.
    const savedAddresses = new Set(destinations.map((d) => d.address));
    const inUse = new Map<string, DestinationRow>();
    for (const address of new Set(holdings.flatMap(effective))) {
        if (!address || savedAddresses.has(address) || inUse.has(address)) {
            continue;
        }
        const routed = routedTo(address);
        const ownWallet = routed
            .map(walletOf)
            .find((account) => account.address === address);
        const accountLabel = ownWallet?.label?.trim();
        inUse.set(address, {
            uuid: `in-use:${address}`,
            address,
            // As the position page names it: the wallet's own label, else
            // "Position Wallet" when rewards go back to the wallet itself.
            label: ownWallet
                ? accountLabel && accountLabel !== address
                    ? accountLabel
                    : POSITION_WALLET_LABEL
                : "",
            usdvBalance: balanceOf(routed),
            positionsUsing: routed.length,
            saved: false
        });
    }

    return [...saved, ...inUse.values()];
}

/** Why a destination's trash is disabled: a position routes rewards to it. */
const IN_USE_REASON = "In Use";

/** The name for a wallet that routes its rewards back to itself. */
const POSITION_WALLET_LABEL = "Position Wallet";

/**
 * What the Name column shows: the custom label, or, when the destination
 * has none, its address shortened to the 4 + 4 form ("9HjD…Gjvj").
 */
function rowName(row: Pick<DestinationRow, "label" | "address">): string {
    return destinationDisplayName(row);
}

/* ── Table view ──────────────────────────────────────────────── */

function DestinationsTable({
    rows,
    viewOnly,
    onDelete
}: {
    rows: DestinationRow[];
    viewOnly: boolean;
    onDelete: (row: DestinationRow) => void;
}): React.ReactElement {
    const [sortColumn, setSortColumn] = useState<SortColumn>("name");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    // In-place rename: one row open at a time. Focus follows the edit, into
    // the input (selected) on open and back to that row's pencil on close.
    const [editing, setEditing] = useState<string | null>(null);
    const [focusTarget, setFocusTarget] = useState<{
        uuid: string;
        on: "input" | "pencil";
    } | null>(null);
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const pencilRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    useEffect(() => {
        if (!focusTarget) return;
        if (focusTarget.on === "input") {
            const input = inputRefs.current[focusTarget.uuid];
            input?.focus();
            input?.select();
        } else {
            pencilRefs.current[focusTarget.uuid]?.focus();
        }
        setFocusTarget(null);
    }, [focusTarget]);

    const handleSort = (column: SortColumn): void => {
        if (column === sortColumn) {
            setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const sortedRows = useMemo(() => {
        const copy = [...rows];
        const cmp = (a: DestinationRow, b: DestinationRow): number => {
            switch (sortColumn) {
                case "name":
                    return rowName(a).localeCompare(rowName(b));
                case "address":
                    return a.address.localeCompare(b.address);
                case "balance":
                    return a.usdvBalance - b.usdvBalance;
                case "positionsUsing":
                    return a.positionsUsing - b.positionsUsing;
            }
        };
        copy.sort((a, b) => (sortDirection === "asc" ? cmp(a, b) : -cmp(a, b)));
        return copy;
    }, [rows, sortColumn, sortDirection]);

    if (sortedRows.length === 0) {
        return (
            <div className="px-6 py-8">
                <Text variant="small" className="text-foreground-muted">
                    No saved rewards destinations yet.
                </Text>
            </div>
        );
    }

    return (
        <div className="border-t border-line-base">
            {/* Column header row */}
            <div className={`${ROW_GRID} px-6 py-2 border-b border-line-base`}>
                <SortableColHead
                    label="Name"
                    columnKey="name"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                />
                <SortableColHead
                    label="Address"
                    columnKey="address"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                />
                <SortableColHead
                    label="USDv Balance"
                    columnKey="balance"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="right"
                />
                <SortableColHead
                    label="Positions Using"
                    columnKey="positionsUsing"
                    activeColumn={sortColumn}
                    direction={sortDirection}
                    onSort={handleSort}
                    align="right"
                />
                <span aria-hidden />
            </div>

            <List>
                {sortedRows.map((row) => (
                    <ListItem
                        key={row.uuid}
                        itemKey={row.uuid}
                        padding="px-6 py-3"
                        gap="gap-0"
                    >
                        <div className={`${ROW_GRID} w-full`}>
                            <div className="min-w-0">
                                <DestinationNameCell
                                    uuid={row.uuid}
                                    address={row.address}
                                    label={row.label}
                                    editing={editing === row.uuid}
                                    viewOnly={viewOnly}
                                    saved={row.saved}
                                    onOpen={() => {
                                        setEditing(row.uuid);
                                        setFocusTarget({
                                            uuid: row.uuid,
                                            on: "input"
                                        });
                                    }}
                                    onClose={() => {
                                        setEditing(null);
                                        setFocusTarget({
                                            uuid: row.uuid,
                                            on: "pencil"
                                        });
                                    }}
                                    inputRef={(element) => {
                                        inputRefs.current[row.uuid] = element;
                                    }}
                                    pencilRef={(element) => {
                                        pencilRefs.current[row.uuid] = element;
                                    }}
                                />
                            </div>
                            <div className="min-w-0">
                                <Address
                                    mono={false}
                                    value={row.address}
                                    textVariant="fine-print"
                                    className="text-foreground-muted font-numeric"
                                />
                            </div>
                            <NumCell>{formatUsd(row.usdvBalance)}</NumCell>
                            <NumCell>{row.positionsUsing}</NumCell>
                            <div className="flex justify-end">
                                {/* Every destination is deletable, but not
                                    while a position routes rewards to it:
                                    the reason is on demand. */}
                                <DisabledReason
                                    reason={
                                        viewOnly
                                            ? VIEW_ONLY_REASON
                                            : row.positionsUsing > 0
                                              ? IN_USE_REASON
                                              : null
                                    }
                                    label="Delete destination"
                                >
                                    <button
                                        type="button"
                                        aria-label="Delete destination"
                                        disabled={
                                            viewOnly || row.positionsUsing > 0
                                        }
                                        onClick={() => onDelete(row)}
                                        className="text-foreground-muted transition-colors hover:text-ember disabled:opacity-40"
                                    >
                                        <TrashIcon />
                                    </button>
                                </DisabledReason>
                            </div>
                        </div>
                    </ListItem>
                ))}
            </List>
        </div>
    );
}

/** Right-aligned numeric table cell — fine-print, tabular. */
function NumCell({
    children
}: {
    children: React.ReactNode;
}): React.ReactElement {
    return (
        <Text
            variant="fine-print"
            render={<span />}
            className="text-foreground-base font-numeric tabular-nums text-right"
        >
            {children}
        </Text>
    );
}

/** Trash glyph for the row delete action — inline so it inherits
 *  `currentColor` (muted → ember on hover). 24×24 source, drawn at 16px. */
function TrashIcon(): React.ReactElement {
    return (
        <svg
            className="size-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M3 6h18" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
        </svg>
    );
}
